// src/cockpit4/components/LeadRoutingPanel.tsx
// ============================================
// P1+ 线索打分/路由：从 /lead-api/list 读留资 → 关键词意向打分 → 属地路由
//   打分规则：need 含"价格/报价/多少钱/购买/开通"=高意向(80)；"咨询/了解一下/方案"=中(50)；"合作/代理/分销"=渠道(70)；其余低(30)
//   路由：按 need/company 地域关键词或行业分派；得分排序
// ============================================

import { useEffect, useState, useMemo } from "react";
import { Route, RefreshCw, Phone } from "lucide-react";

interface Lead { name: string; phone: string; company: string; need: string; source: string; time: string; }
interface Scored extends Lead { score: number; level: string; route: string; }

const HIGH_WORDS = ["价格", "报价", "多少钱", "购买", "开通", "下单", "付费", "马上", "急"];
const MID_WORDS = ["咨询", "了解", "方案", "演示", "看看", "对比", "合适"];
const CHANNEL_WORDS = ["代理", "分销", "合作", "加盟", "白标", "贴牌"];
const REGION_WORDS: Record<string, string> = {
  "长沙": "湖南-长沙", "湖南": "湖南", "北京": "北京", "上海": "上海", "广州": "广东", "深圳": "广东",
  "成都": "四川", "武汉": "湖北", "杭州": "浙江", "西安": "陕西", "重庆": "重庆", "酒店": "酒店行业线", "民宿": "酒旅行业线",
};

function scoreLead(l: Lead): Scored {
  const t = `${l.need} ${l.company}`;
  let score = 30; let level = "低意向";
  if (HIGH_WORDS.some((w) => t.includes(w))) { score = 80; level = "高意向"; }
  else if (CHANNEL_WORDS.some((w) => t.includes(w))) { score = 70; level = "渠道"; }
  else if (MID_WORDS.some((w) => t.includes(w))) { score = 50; level = "中意向"; }
  // 路由：地域 / 行业
  let route = "默认承接";
  for (const [k, v] of Object.entries(REGION_WORDS)) {
    if (t.includes(k)) { route = v; break; }
  }
  if (level === "渠道") route = "渠道部（代理分销）";
  return { ...l, score, level, route };
}

export default function LeadRoutingPanel() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch("/lead-api/list");
      const d = await r.json();
      if (d?.success) setLeads(Array.isArray(d.leads) ? d.leads : []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const scored = useMemo(() => leads.map(scoreLead).sort((a, b) => b.score - a.score), [leads]);
  const highCount = scored.filter((s) => s.score >= 70).length;

  return (
    <div className="space-y-2">
      <div className="text-[10px] font-mono text-gray-400 flex items-center gap-1.5">
        <Route size={11} className="text-emerald-400" />
        线索打分/路由：<span className="text-white">{scored.length}</span> 条 · 高意向/渠道 <span className="text-emerald-400">{highCount}</span> 条
        <button onClick={load} disabled={loading} className="ml-auto flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] text-gray-400 border border-white/[0.08]">
          <RefreshCw size={8} className={loading ? "animate-spin" : ""} /> 刷新
        </button>
      </div>

      {scored.length === 0 ? (
        <p className="text-[10px] text-gray-600 text-center py-4">暂无线索。官网留资提交后会自动打分路由。</p>
      ) : (
        <div className="space-y-1 max-h-[340px] overflow-y-auto">
          {scored.map((l, i) => (
            <div key={i} className="rounded border border-white/[0.05] bg-white/[0.02] p-2">
              <div className="flex items-center gap-2">
                <span className={`text-[8px] font-mono font-bold px-1 rounded ${l.score >= 70 ? "bg-emerald-500/15 text-emerald-400" : l.score >= 50 ? "bg-amber-500/15 text-amber-400" : "bg-gray-500/15 text-gray-400"}`}>
                  {l.score} · {l.level}
                </span>
                <span className="text-[11px] font-bold text-white">{l.name}</span>
                <a href={`tel:${l.phone}`} className="text-[9px] text-cyan-300 hover:underline">{l.phone}</a>
                <span className="ml-auto text-[8px] font-mono text-gray-600">{(l.time || "").slice(5, 16)}</span>
              </div>
              {l.need && <p className="text-[9px] text-gray-400 mt-0.5">{l.need}</p>}
              <div className="flex items-center gap-2 mt-1 text-[8px] font-mono">
                <span className="text-blue-300">路由：{l.route}</span>
                {l.company && <span className="text-gray-600">公司：{l.company}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="rounded border border-white/[0.06] bg-white/[0.02] p-2">
        <div className="text-[9px] font-mono text-gray-400 mb-1">打分规则</div>
        <ul className="text-[8px] text-gray-500 space-y-0.5">
          <li>· 高意向(80)：含 价格/报价/购买/开通/付费</li>
          <li>· 渠道(70)：含 代理/分销/加盟/白标</li>
          <li>· 中意向(50)：含 咨询/方案/演示/对比</li>
          <li>· 低意向(30)：其余</li>
          <li>· 路由：按地域/行业关键词分派，渠道类直送渠道部</li>
        </ul>
      </div>
    </div>
  );
}
